import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import belowIcon from '../../../asset/icon/below.png';
import { PaperBlurFitBtn } from '../../../shared/component/headless/button/BtnBody';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import {
  SearchMessage,
  SearchResults,
} from '../../../shared/component/modal/SearchModalLayout';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import { getSettingsMessage } from '../../../shared/constants/pageMessages';
import { AlertPosition, AlertType } from '../../../shared/enum/EnumRepository';
import { useLinkedGroupMember } from '../../../shared/hooks/useApi/useLinkedGroupMember';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { useModalStore } from '../../../shared/store/portal/modalStore';

interface LinkedGroupMemberManagementModalMobileProps {
  setIsUserInput?: (isUserInput: boolean) => void;
}

const LinkedGroupMemberManagementModalMobile: React.FC<LinkedGroupMemberManagementModalMobileProps> = ({
  setIsUserInput,
}) => {
  const { connections, status, unlink, getConnections } = useLinkedGroupMember();
  const { clearModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();

  useEffect(() => {
    setIsUserInput?.(false);
    return () => setIsUserInput?.(false);
  }, [setIsUserInput]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearModal(400);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearModal]);

  const handleClose = () => clearModal(400);

  const handleUnlink = (groupMemberId: string, groupName: string) => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_UNLINK_LINKED_GROUP_MEMBER', { groupName }),
      callBack: async () => {
        await unlink(groupMemberId);
      },
    });
  };

  return (
    <MobileBody>
      <TitleWrapper>
        <Title>{getSettingsMessage('SETTINGS_CONNECTIONS')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </TitleWrapper>
      <ConnectionList>
        {(status === 'idle' || status === 'loading') && (
          <SearchMessage>{getSettingsMessage('CONNECTION_LOADING')}</SearchMessage>
        )}
        {status === 'error' && (
          <ErrorMessage role="alert">
            <span>{getSettingsMessage('CONNECTION_FAILED')}</span>
            <RetryButton onClick={() => void getConnections()}>
              {getBtnName('RETRY')}
            </RetryButton>
          </ErrorMessage>
        )}
        {status === 'success' && connections.length === 0 && (
          <SearchMessage>{getSettingsMessage('CONNECTION_NONE')}</SearchMessage>
        )}
        {connections.map(({ groupAui, groupName, groupHandle, groupMember }) => (
          <ConnectionItem key={`${groupAui}-${groupMember.id}`}>
            <ConnectionInfo>
              <GroupArchiveLink
                to={`/@${groupHandle}/`}
                onClick={handleClose}
              >
                {groupName}
              </GroupArchiveLink>
              <ConnectionMeta>@{groupHandle}</ConnectionMeta>
            </ConnectionInfo>
            <ActionWrap>
              <HeadlessBtn
                value={getBtnName('UNLINK')}
                handleClick={() => handleUnlink(groupMember.id, groupName.trim() || groupHandle)}
                StyledBtn={UnlinkButton}
              />
            </ActionWrap>
          </ConnectionItem>
        ))}
      </ConnectionList>
    </MobileBody>
  );
};

const MobileBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  max-height: calc(100dvh - 40px);
  padding-bottom: 20px;
  overflow: hidden;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const ConnectionList = styled(SearchResults)`
  min-height: 0;
  max-height: none;
  flex: 1;
`;

const ConnectionItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  box-sizing: border-box;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
`;

const ConnectionInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GroupArchiveLink = styled(Link)`
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  text-decoration: underline;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const ConnectionMeta = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const ActionWrap = styled.div`
  flex-shrink: 0;
`;

const ErrorMessage = styled(SearchMessage)`
  flex-direction: column;
  gap: 10px;
`;

const UnlinkButton = styled(PaperBlurFitBtn)`
  padding: 8px 1rem;
`;

const RetryButton = styled(UnlinkButton)`
  width: 100%;
  margin: 20px 0px 15px 0px;
`;

export default LinkedGroupMemberManagementModalMobile;
